import React, { useState } from "react";
import {
  FaChartLine,
  FaWeight,
  FaPlus,
  FaHeartbeat,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaCalculator,
} from "react-icons/fa";
import useStore from "services/store";
import { TrackProgress } from "./trackProgress";

export const ProgressDashboard = () => {
  const { user, metrics } = useStore();
  const [activeTab, setActiveTab] = useState("track");

  const MetricCard = ({ title, value, change, trend, icon, color }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div className={`text-2xl ${color}`}>{icon}</div>
        <div className="text-right">
          <div className="text-lg font-bold text-black">{value}</div>
          {change && (
            <div
              className={`flex items-center ${
                trend === "up" ? "text-green-500" : "text-red-500"
              }`}
            >
              {trend === "up" ? (
                <FaArrowUp className="mr-1" size={12} />
              ) : (
                <FaArrowDown className="mr-1" size={12} />
              )}
              <span className="text-xs">{Math.abs(change)}</span>
            </div>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-500">{title}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black mb-2">
            Progress Dashboard
          </h1>
          <p className="text-gray-600">Track your fitness progress</p>
        </div>

        {/* Metrics Overview */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-black mb-4 flex items-center">
            <FaChartLine className="mr-2 text-blue-500" size={20} />
            Current Metrics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Weight (kg)"
              value={user?.current_weight || "-"}
              icon={<FaWeight size={20} />}
              color="text-red-500"
            />
            <MetricCard
              title="Body Fat (%)"
              value={user?.body_fat || "-"}
              icon={<FaHeartbeat size={20} />}
              color="text-orange-500"
            />
            <MetricCard
              title="Time to Goal (weeks)"
              value={metrics?.estimated_time_to_goal || "-"}
              icon={<FaClock size={20} />}
              color="text-yellow-500"
            />
            <MetricCard
              title="BMI"
              value={metrics?.bmi?.toFixed(1) || "-"}
              icon={<FaCalculator size={20} />}
              color="text-purple-500"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("track")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "track"
                    ? "border-gray-500 text-gray-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FaPlus className="inline mr-2" size={16} />
                Track Your Progress
              </button>
              <button
                onClick={() => setActiveTab("dashboard")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "dashboard"
                    ? "border-gray-500 text-gray-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FaChartLine className="inline mr-2" size={16} />
                Progress Overview
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "track" ? (
              <TrackProgress />
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-black">
                    Progress History
                  </h3>
                  <button
                    onClick={() => setActiveTab("track")}
                    className="text-gray-600 hover:text-gray-700 text-sm font-medium"
                  >
                    Update Progress
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-8 text-center border border-gray-200 shadow-sm">
                    <FaChartLine className="text-4xl text-black mx-auto mb-4" size={48} />
                    <h3 className="text-xl font-semibold text-black mb-2">
                      Charts Coming Soon
                    </h3>
                    <p className="text-black mb-4">
                      We're working on beautiful charts to visualize your
                      progress!
                    </p>
                    <div className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-full text-sm font-medium">
                      <FaChartLine className="mr-2" size={16} />
                      In Development
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
